import type { SimpleTableConfig } from "@/components/shared/UniversalCRMTable/types";
import type { DetailPageConfig } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { BaseEntity, FieldValue, EntityWithRelations } from "@/lib/types/table/shared";

// ============================================================================
// ENTITY REGISTRY TYPES
// ============================================================================

/**
 * Function that resolves the display value for a table cell field.
 * Each entity owns this function — handles relation fields and denormalized synthesis
 * without a global switch statement. Falls back to direct property access.
 *
 * Accepts EntityWithRelations<TData> so relation fields (assigned_users, contacts,
 * companies, etc.) are typed without needing `(item as any)` at each call site.
 */
export type SynthesizeFieldFn<TData extends BaseEntity> = (
  item: EntityWithRelations<TData>,
  fieldId: string
) => FieldValue | Record<string, unknown> | undefined;

/**
 * Complete definition of a CRM entity's UI configuration.
 *
 * Co-locates what was previously scattered across:
 * - Entity*Config.ts (table config, fields, savedViews, filterHooks)
 * - EntityDetailConfig.ts (detail page config)
 * - tableCellFieldValue.ts (field value synthesis)
 * - config-enhancers.ts (dynamic field option injection)
 */
export interface EntityDefinition<TData extends BaseEntity> {
  /**
   * Full table config: fields, actions, savedViews, customFilterHooks, features.
   * Was: EnhancedContactsConfig.ts, contact-saved-views.ts, contact-custom-filter-hooks.ts
   */
  tableConfig: SimpleTableConfig<TData>;

  /**
   * Detail page config: tabs, actions, breadcrumbs, title/description templates.
   * Was: ContactDetailConfig.ts
   */
  detailConfig: DetailPageConfig<TData>;

  /**
   * Entity-owned field value synthesis.
   * Resolves relation fields (company_id → company object), denormalized fields, etc.
   * Was: per-entity cases in tableCellFieldValue.ts getFieldValue().
   *
   * Receives EntityWithRelations<TData> so relation keys are typed — no casts needed.
   */
  synthesizeField: SynthesizeFieldFn<TData>;

  /**
   * Reference to a React hook that enhances tableConfig with dynamic data.
   * Called by the page component to inject tag options, technology options, etc.
   * Was: useContactsConfigEnhancer in config-enhancers.ts.
   *
   * HOOKS RULE: callers must invoke this at the top level of a component/hook.
   * If undefined, tableConfig is used as-is.
   */
  useConfigEnhancer?: (config: SimpleTableConfig<TData>) => SimpleTableConfig<TData>;
}

/**
 * Identity function with generic inference.
 * Wrap entity config objects with this to get full type checking
 * and a consistent interface across all CRM entities.
 */
export function defineEntity<TData extends BaseEntity>(
  def: EntityDefinition<TData>
): EntityDefinition<TData> {
  return def;
}
